import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { getSurvey, createSurvey, updateSurvey, getSurveyResponses } from '@/lib/db'
import type { SurveyQuestion, SurveyQuestionType, SurveyResponse } from '@/types'
import {
  Loader2, ChevronLeft, Save, Plus, Trash2, ClipboardList,
  AlertCircle, ChevronDown, ChevronUp, FileBarChart
} from 'lucide-react'

function uid() { return Math.random().toString(36).slice(2, 10) }

const QUESTION_TYPES: { value: SurveyQuestionType; label: string; description: string }[] = [
  { value: 'single_choice', label: 'Opción única', description: 'El alumno elige una opción' },
  { value: 'multiple_choice', label: 'Opción múltiple', description: 'El alumno puede elegir varias' },
  { value: 'text', label: 'Texto libre', description: 'El alumno escribe su respuesta' },
  { value: 'rating', label: 'Calificación (estrellas)', description: 'De 1 a 5 estrellas' },
  { value: 'scale', label: 'Escala (1-10)', description: 'Puntuación del 1 al 10' },
]

export default function SurveyEditor() {
  const { surveyId } = useParams()
  const navigate = useNavigate()
  const isNew = !surveyId || surveyId === 'new'

  const [loading, setLoading] = useState(!isNew)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [isActive, setIsActive] = useState(true)
  const [questions, setQuestions] = useState<SurveyQuestion[]>([])

  const [tab, setTab] = useState<'edit' | 'responses'>('edit')
  const [responses, setResponses] = useState<SurveyResponse[]>([])
  const [loadingResponses, setLoadingResponses] = useState(false)
  const [expandedQ, setExpandedQ] = useState<string | null>(null)

  useEffect(() => {
    async function load() {
      if (!isNew) {
        const { data } = await getSurvey(surveyId!)
        if (data) {
          setTitle(data.title)
          setDescription(data.description || '')
          setIsActive(data.is_active)
          setQuestions(data.questions || [])
        }
      }
      setLoading(false)
    }
    load()
  }, [surveyId, isNew])

  async function loadResponses() {
    if (isNew) return
    setLoadingResponses(true)
    const { data } = await getSurveyResponses(surveyId!)
    if (data) setResponses(data)
    setLoadingResponses(false)
  }

  useEffect(() => {
    if (tab === 'responses' && !isNew) loadResponses()
  }, [tab])

  function addQuestion(type: SurveyQuestionType) {
    const q: SurveyQuestion = {
      id: uid(),
      type,
      question: '',
      required: true,
      options: type === 'single_choice' || type === 'multiple_choice' ? [''] : undefined,
    }
    setQuestions(prev => [...prev, q])
    setExpandedQ(q.id)
  }

  function updateQuestion(id: string, partial: Partial<SurveyQuestion>) {
    setQuestions(prev => prev.map(q => q.id === id ? { ...q, ...partial } : q))
  }

  function removeQuestion(id: string) {
    setQuestions(prev => prev.filter(q => q.id !== id))
  }

  function moveQuestion(idx: number, dir: -1 | 1) {
    setQuestions(prev => {
      const arr = [...prev]
      const newIdx = idx + dir
      if (newIdx < 0 || newIdx >= arr.length) return prev
      ;[arr[idx], arr[newIdx]] = [arr[newIdx], arr[idx]]
      return arr
    })
  }

  async function handleSave() {
    if (!title.trim()) { setError('Ingresa un título'); return }
    if (questions.length === 0) { setError('Agrega al menos una pregunta'); return }

    setSaving(true)
    setError('')

    if (isNew) {
      const { error: createErr } = await createSurvey({
        title: title.trim(),
        description: description.trim() || undefined,
        questions,
        is_active: isActive,
      })
      if (createErr) { setError((createErr as any)?.message || 'Error'); setSaving(false); return }
    } else {
      const { error: updateErr } = await updateSurvey(surveyId!, {
        title: title.trim(),
        description: description.trim() || null,
        questions,
        is_active: isActive,
      } as any)
      if (updateErr) { setError((updateErr as any)?.message || 'Error'); setSaving(false); return }
    }

    setSaving(false)
    navigate('/admin/flows')
  }

  if (loading) {
    return <div className="flex items-center justify-center min-h-[400px]"><Loader2 className="w-8 h-8 animate-spin text-indigo-500" /></div>
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <button onClick={() => navigate('/admin/flows')} className="text-sm text-gray-500 hover:text-gray-900 mb-3 flex items-center gap-1">
          <ChevronLeft className="w-4 h-4" /> Volver
        </button>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <ClipboardList className="w-7 h-7 text-indigo-400" />
          {isNew ? 'Crear encuesta' : 'Editar encuesta'}
        </h1>
      </div>

      {/* Tabs for edit/responses */}
      {!isNew && (
        <div className="flex gap-1 bg-white rounded-lg p-1 w-fit">
          <button onClick={() => setTab('edit')}
            className={`px-4 py-2 rounded-md text-sm font-medium ${tab === 'edit' ? 'bg-[#0055A4] text-white' : 'text-gray-500 hover:text-gray-900'}`}>
            Editar
          </button>
          <button onClick={() => setTab('responses')}
            className={`px-4 py-2 rounded-md text-sm font-medium flex items-center gap-1 ${tab === 'responses' ? 'bg-[#0055A4] text-white' : 'text-gray-500 hover:text-gray-900'}`}>
            <FileBarChart className="w-4 h-4" /> Respuestas ({responses.length})
          </button>
        </div>
      )}

      {tab === 'edit' && (
        <>
          {/* General info */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="survey-title" className="block text-sm text-gray-500 mb-1">Título de la encuesta *</label>
                <input id="survey-title" value={title} onChange={e => setTitle(e.target.value)} placeholder="Encuesta de satisfacción"
                  className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-900" />
              </div>
              <div>
                <span className="block text-sm text-gray-500 mb-1">Estado</span>
                <button onClick={() => setIsActive(!isActive)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium w-full ${isActive ? 'bg-green-100 text-green-700 border border-green-300' : 'bg-gray-100 text-gray-500 border border-gray-300'}`}>
                  {isActive ? 'Activa' : 'Inactiva'}
                </button>
              </div>
            </div>
            <div>
              <label htmlFor="survey-description" className="block text-sm text-gray-500 mb-1">Descripción</label>
              <textarea id="survey-description" value={description}  onChange={e => setDescription(e.target.value)} rows={2}
                placeholder="Breve descripción de la encuesta..."
                className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-900 resize-none" />
            </div>
          </div>

          {/* Questions */}
          <div className="bg-white rounded-xl border border-indigo-200 p-5 space-y-4">
            <h2 className="text-sm font-bold text-indigo-400 uppercase tracking-wide">
              Preguntas ({questions.length})
            </h2>

            {questions.map((q, idx) => {
              const isExpanded = expandedQ === q.id
              const typeInfo = QUESTION_TYPES.find(t => t.value === q.type)

              return (
                <div key={q.id} className={`border rounded-xl transition-all ${isExpanded ? 'border-indigo-700' : 'border-gray-200'}`}>
                  <div
                    className="flex items-center gap-3 px-4 py-3 cursor-pointer"
                    role="button"
                    tabIndex={0}
                    onClick={() => setExpandedQ(isExpanded ? null : q.id)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setExpandedQ(isExpanded ? null : q.id) }}
                  >
                    <div className="flex flex-col gap-0.5">
                      <button onClick={e => { e.stopPropagation(); moveQuestion(idx, -1) }} disabled={idx === 0} className="text-gray-500 hover:text-gray-900 disabled:opacity-20"><ChevronUp className="w-3 h-3" /></button>
                      <button onClick={e => { e.stopPropagation(); moveQuestion(idx, 1) }} disabled={idx === questions.length - 1} className="text-gray-500 hover:text-gray-900 disabled:opacity-20"><ChevronDown className="w-3 h-3" /></button>
                    </div>
                    <span className="text-xs font-mono text-gray-500 w-5">{idx + 1}</span>
                    <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full">{typeInfo?.label}</span>
                    <span className="flex-1 text-sm text-gray-900 truncate">{q.question || '(sin pregunta)'}</span>
                    {q.required && <span className="text-xs text-red-600">*</span>}
                    <button onClick={e => { e.stopPropagation(); removeQuestion(q.id) }} className="p-1 text-red-600 hover:text-red-300"><Trash2 className="w-4 h-4" /></button>
                  </div>

                  {isExpanded && (
                    <div className="px-4 pb-4 border-t border-gray-200 pt-3 space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="col-span-2">
                          <label htmlFor={`survey-q-${q.id}`} className="block text-xs font-bold text-gray-500 mb-1">Pregunta</label>
                          <input id={`survey-q-${q.id}`} value={q.question} onChange={e => updateQuestion(q.id, { question: e.target.value })}
                            placeholder="¿Cómo calificarías tu experiencia?"
                            className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-900 text-sm" />
                        </div>
                        <div>
                          <label htmlFor={`survey-type-${q.id}`} className="block text-xs font-bold text-gray-500 mb-1">Tipo</label>
                          <select id={`survey-type-${q.id}`} value={q.type} onChange={e => updateQuestion(q.id, {
                            type: e.target.value as SurveyQuestionType,
                            options: ['single_choice', 'multiple_choice'].includes(e.target.value) ? (q.options || ['']) : undefined
                          })} className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-900 text-sm">
                            {QUESTION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                          </select>
                        </div>
                        <div className="flex items-end">
                          <label className="flex items-center gap-2 text-sm text-gray-500 cursor-pointer">
                            <input type="checkbox" checked={q.required} onChange={e => updateQuestion(q.id, { required: e.target.checked })}
                              className="rounded border-gray-500 text-indigo-500" />{' '}
                            Obligatoria
                          </label>
                        </div>
                      </div>

                      {/* Options for choice questions */}
                      {(q.type === 'single_choice' || q.type === 'multiple_choice') && (
                        <div>
                          <span className="block text-xs font-bold text-gray-500 mb-2">Opciones</span>
                          <div className="space-y-2">
                            {(q.options || []).map((opt, oi) => (
                              <div key={oi} className="flex items-center gap-2">
                                <span className="text-xs text-gray-500 w-5">{oi + 1}</span>
                                <input value={opt} onChange={e => {
                                  const opts = [...(q.options || [])]
                                  opts[oi] = e.target.value
                                  updateQuestion(q.id, { options: opts })
                                }} placeholder={`Opción ${oi + 1}`}
                                  className="flex-1 px-3 py-1.5 bg-gray-100 border border-gray-300 rounded-lg text-gray-900 text-sm" />
                                <button onClick={() => {
                                  const opts = (q.options || []).filter((_, i) => i !== oi)
                                  updateQuestion(q.id, { options: opts })
                                }} className="p-1 text-red-600 hover:text-red-300"><Trash2 className="w-3.5 h-3.5" /></button>
                              </div>
                            ))}
                            <button onClick={() => updateQuestion(q.id, { options: [...(q.options || []), ''] })}
                              className="text-xs text-indigo-400 hover:text-indigo-700 flex items-center gap-1">
                              <Plus className="w-3 h-3" /> Agregar opción
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )
            })}

            {/* Add question */}
            <div>
              <p className="text-xs text-gray-500 mb-2">Agregar pregunta:</p>
              <div className="flex flex-wrap gap-2">
                {QUESTION_TYPES.map(t => (
                  <button key={t.value} onClick={() => addQuestion(t.value)}
                    className="text-xs px-3 py-1.5 rounded-lg bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-200 transition-colors">
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-red-600 text-sm flex items-center gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
            </div>
          )}

          <div className="flex justify-end gap-3">
            <button onClick={() => navigate('/admin/flows')} className="px-4 py-2 bg-gray-100 text-gray-900 rounded-lg hover:bg-gray-200">Cancelar</button>
            <button onClick={handleSave} disabled={saving}
              className="px-6 py-2 bg-[#0055A4] text-white rounded-lg hover:bg-[#004080] disabled:opacity-50 flex items-center gap-2 font-medium">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {saving ? 'Guardando...' : isNew ? 'Crear encuesta' : 'Guardar'}
            </button>
          </div>
        </>
      )}

      {/* Responses tab */}
      {tab === 'responses' && (
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          {loadingResponses ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-indigo-400" /></div>
          ) : responses.length === 0 ? (
            <div className="text-center py-8">
              <FileBarChart className="w-12 h-12 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-500">No hay respuestas aún</p>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-gray-500">{responses.length} respuestas recibidas</p>
              {responses.map((resp, ri) => (
                <div key={resp.id} className="bg-gray-100/50 rounded-lg p-4 border border-gray-200">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm text-gray-900 font-medium">Respuesta #{ri + 1}</span>
                    <span className="text-xs text-gray-500">{new Date(resp.completed_at).toLocaleString('es-CL')}</span>
                  </div>
                  <div className="space-y-2">
                    {questions.map(q => (
                      <div key={q.id} className="flex gap-2">
                        <span className="text-xs text-gray-500 flex-shrink-0 w-1/3 truncate">{q.question}:</span>
                        <span className="text-sm text-gray-900">{formatAnswer(resp.answers[q.id], q.type)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function formatAnswer(answer: any, type: string): string {
  if (answer === undefined || answer === null) return '-'
  if (Array.isArray(answer)) return answer.join(', ')
  if (type === 'rating') return '⭐'.repeat(Number(answer) || 0)
  return String(answer)
}
